import { ChevronDown, ChevronRight, Code2, Eye } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "@bigpp/ui/lib/utils";

// Render a model-emitted HTML snippet in a tightly-sandboxed iframe so the
// user can see a visual draft without any script-execution risk. We provide
// a minimal stylesheet shim so common Tailwind-ish utility classes (flex,
// gap, p-*, text-*) render approximately the way the model expects, but the
// preview is meant for sketches — not pixel-perfect renders.
export function JsxPreview({ title, html }: { title?: string; html: string }) {
  const [tab, setTab] = useState<"preview" | "code">("preview");
  const [open, setOpen] = useState(true);
  const srcDoc = useMemo(() => buildSrcDoc(html), [html]);
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-[var(--color-border)]">
        <button
          type="button"
          onClick={() => {
            setOpen((o) => !o);
          }}
          className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-foreground)] hover:opacity-80"
        >
          <Eye className="size-3.5 text-[var(--color-muted-foreground)]" />
          <span className="truncate">{title ?? "Preview"}</span>
          {open ? (
            <ChevronDown className="size-3 opacity-60" />
          ) : (
            <ChevronRight className="size-3 opacity-60" />
          )}
        </button>
        {open && (
          <div className="ml-auto flex items-center gap-1 rounded border border-[var(--color-border)] p-0.5">
            <TabButton active={tab === "preview"} onClick={() => { setTab("preview"); }}>
              <Eye className="size-3" />
              Preview
            </TabButton>
            <TabButton active={tab === "code"} onClick={() => { setTab("code"); }}>
              <Code2 className="size-3" />
              Code
            </TabButton>
          </div>
        )}
      </div>
      {open && (
        <div>
          {tab === "preview" ? (
            <iframe
              title={title ?? "JSX preview"}
              sandbox=""
              srcDoc={srcDoc}
              className="block w-full h-64 bg-white"
            />
          ) : (
            <pre className="m-0 max-h-64 overflow-auto p-2 text-[11px] whitespace-pre-wrap break-words font-mono bg-[var(--color-secondary)]">
              {html}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]",
        active
          ? "bg-[var(--color-secondary)] text-[var(--color-foreground)]"
          : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
      )}
    >
      {children}
    </button>
  );
}

const BASE_STYLES = `
  html, body { margin: 0; padding: 0.75rem; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #18181b; background: #fff; font-size: 14px; line-height: 1.4; }
  * { box-sizing: border-box; }
  img { max-width: 100%; height: auto; }
`;

const buildSrcDoc = (html: string): string => {
  // `sandbox=""` on the iframe already blocks scripts, forms, top-nav, etc.
  // We strip <script> defensively too so the code-view doesn't display
  // anything the model couldn't have run anyway.
  const safe = html.replace(/<script[\s\S]*?<\/script>/gi, "");
  return `<!doctype html><html><head><meta charset="utf-8"><style>${BASE_STYLES}</style></head><body>${safe}</body></html>`;
};
